import { useEffect, useRef } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth, dashboardPath } from "./context/AuthContext";
import api from "./lib/api";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Marketplace from "./pages/Marketplace";
import ProductDetail from "./pages/ProductDetail";
import SellerDashboard from "./pages/SellerDashboard";
import DriverDashboard from "./pages/DriverDashboard";
import BuyerInquiries from "./pages/BuyerInquiries";
import AdminDashboard from "./pages/AdminDashboard";
import Transport from "./pages/Transport";
import CompanyDashboard from "./pages/CompanyDashboard";
import TransporterProfile from "./pages/TransporterProfile";
import FindTransporter from "./pages/FindTransporter";
import { Impressum, Datenschutz, Terms } from "./pages/LegalPages";
import { FloatingWhatsApp } from "./components/FloatingWhatsApp";
import { PrivateBetaBanner } from "./components/PrivateBetaBanner";

function FullScreenLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="loading-screen">
      <div className="font-display text-2xl tracking-tight text-primary animate-pulse">WILTOM TradeLink</div>
    </div>
  );
}

function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const hash = window.location.hash;
    const sessionId = new URLSearchParams(hash.replace("#", "")).get("session_id");
    const role = sessionStorage.getItem("pending_role") || "buyer";
    (async () => {
      try {
        const { data } = await api.post("/auth/session", { session_id: sessionId, role });
        localStorage.setItem("wt_session", "1");
        setUser(data.user);
        sessionStorage.removeItem("pending_role");
        window.history.replaceState(null, "", window.location.pathname);
        navigate(dashboardPath(data.user.role), { replace: true });
      } catch {
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, setUser]);

  return <FullScreenLoader />;
}

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={dashboardPath(user.role)} replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
      <PrivateBetaBanner />
      <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/marketplace" element={<Marketplace />} />
      <Route path="/transport" element={<Transport />} />
      <Route path="/find-transporter" element={<FindTransporter />} />
      <Route path="/transporter/:id" element={<TransporterProfile />} />
      <Route path="/product/:id" element={<ProductDetail />} />
      <Route path="/impressum" element={<Impressum />} />
      <Route path="/datenschutz" element={<Datenschutz />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/seller" element={<ProtectedRoute roles={["seller"]}><SellerDashboard /></ProtectedRoute>} />
      <Route path="/company" element={<ProtectedRoute roles={["company"]}><CompanyDashboard /></ProtectedRoute>} />
      <Route path="/driver" element={<ProtectedRoute roles={["driver"]}><DriverDashboard /></ProtectedRoute>} />
      <Route path="/inquiries" element={<ProtectedRoute roles={["buyer", "seller", "driver"]}><BuyerInquiries /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><AdminDashboard /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-right" richColors />
          <AppRouter />
          <FloatingWhatsApp />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
